import React, { useContext, useEffect, useState } from "react";

import { Preview } from "@creatomate/preview";

import { ElementSource, MediaVersion, Organization } from "chq";
import { useProfile } from "app/context";

import getDefaultSource from "../utils/creatomate/getDefaultSource";
import { CreatomateContext } from "../contexts/VideoStudioContext";
import InteractivePlayer from "./InteractivePlayer";
import { loadSubtitles } from "../sidebar/tabs/subtitle/utils";
import { loadLatestFontsInJson } from "../utils";
import { VIDEO_TYPE } from "../utils/constants";
import StaticPlayer from "./StaticPlayer";
import { usePreviewTime } from "../contexts/TimeContext";

interface CreatomatePlayerProps {
  creatomateJson?: Record<string, any>;
  mode?: "player" | "interactive";
  onPauseVideo?: (value?: boolean) => void;
  pausedVideo?: boolean;
  previewRef: React.MutableRefObject<Preview | undefined>;
  outputFormat?: string;
}

const CreatomatePlayer: React.FC<CreatomatePlayerProps> = ({
  creatomateJson, mode, onPauseVideo, pausedVideo, previewRef, outputFormat
}) => {
  const { profile: { organization } } = useProfile();
  const { setTime } = usePreviewTime();

  const {
    data,
    handleUpdateData,
    handleDebouncedAutosaveDraft,
    getBurnSubtitles
  } = useContext(CreatomateContext);
  const { aspectRatio, story, subtitles, selectedMediaVersion, templateId } = data;

  // Current state of the player
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const setSource = async () => {
      if (!previewRef.current || !creatomateJson) return;

      await previewRef.current.setSource(creatomateJson);
    };

    if (mode === "player" && previewRef.current?.ready) setSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatomateJson, mode]);

  useEffect(() => {
    if (pausedVideo && previewRef.current && previewRef.current.ready) previewRef.current.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pausedVideo, previewRef.current]);

  // This sets up the video player in the provided HTML DIV element
  const handleSetupPlayer = (htmlElement: HTMLDivElement) => {
    if (previewRef.current) {
      previewRef.current.dispose();
      // eslint-disable-next-line no-param-reassign
      previewRef.current = undefined;
    }

    // Initialize a preview
    const preview = new Preview(htmlElement, mode || "interactive", "public-pbkldankjxwdzgi06cgvydm0");

    // Once the SDK is ready, load a template from our project
    preview.onReady = async () => {
      await preview.setZoom("centered");
      await preview.setCacheBypassRules([
        /^https:\/\/culturehq-direct-uploads\.s3-us-west-2\.amazonaws\.com\//,
        /^https:\/\/culturehq-direct-uploads-ireland\.s3-eu-west-1\.amazonaws\.com\//,
        /^https:\/\/culturehq-direct-uploads-staging\.s3-us-west-2\.amazonaws\.com\//
      ]);
      if (templateId) {
        await preview.loadTemplate(templateId);
      } else if (creatomateJson && creatomateJson.elements?.length > 0) {
        const firstVideo = creatomateJson.elements.filter(
          (element: ElementSource) => element.type === VIDEO_TYPE
        ).sort((a: ElementSource, b: ElementSource) => a.time - b.time)[0];
        handleUpdateData({ originalTrimStart: firstVideo?.trim_start || 0 });
        const creatomateJsonWithLatestFonts = organization
          ? loadLatestFontsInJson(creatomateJson, organization as Organization) : creatomateJson;
        await preview.setSource(creatomateJsonWithLatestFonts);
      } else if (mode !== "player") {
        await preview.setSource(
          await getDefaultSource({
            organization: organization as Organization,
            preview,
            story,
            aspectRatio,
            outputFormat
          })
        );
      }
      if (subtitles.length > 0) {
        const updatedSubtitles = await loadSubtitles({
          subtitles,
          organizationName: organization?.name || "",
          preview,
          onUpdateSubtitles: handleDebouncedAutosaveDraft,
          burnSubtitles: getBurnSubtitles(selectedMediaVersion as MediaVersion)
        });
        handleDebouncedAutosaveDraft({ subtitles: updatedSubtitles });
      }
    };

    preview.onLoad = () => {
      setIsLoading(true);
    };

    preview.onLoadComplete = () => {
      setIsLoading(false);
    };

    // Listen for state changes of the player
    preview.onStateChange = state => {
      handleUpdateData({
        aspectRatio: state.width / state.height,
        creatomateJson: state,
        fillColor: state.source.fill_color,
        elements: state.elements
      });
      handleDebouncedAutosaveDraft({ creatomateJson: state });
    };

    preview.onPause = () => {
      handleUpdateData({ isPlaying: false });
    };

    preview.onPlay = () => {
      if (onPauseVideo) onPauseVideo(false);
      handleUpdateData({ isPlaying: true });
    };

    preview.onTimeChange = (time: number) => {
      setTime(time);
    };

    preview.onActiveElementsChange = elementsIds => {
      handleUpdateData({ activeElementsId: elementsIds });
    };

    // eslint-disable-next-line no-param-reassign
    previewRef.current = preview;
  };

  if (mode === "player") {
    return (
      <StaticPlayer
        isLoading={isLoading}
        onSetupPlayer={handleSetupPlayer}
        previewRef={previewRef}
      />
    );
  }

  return (
    <InteractivePlayer
      isLoading={isLoading}
      onSetupPlayer={handleSetupPlayer}
      previewRef={previewRef}
    />
  );
};

export default CreatomatePlayer;
